import { useQuery } from "@apollo/client";
import { orderBy, sortBy } from "lodash";
import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  GetCareerRoleRelatedDataQueryQuery,
  GetCareerRoleRelatedDataQueryQueryVariables,
  GetUserRolesDetailsQueryQuery,
  GetUserRolesDetailsQueryQueryVariables,
} from "types/graphql-schema";
import { BasicUser, TFLocationState } from "types/topicflow";

import getUserRolesDetailsQuery from "@apps/assessments/graphql/get-user-roles-details-query";
import {
  mapCareerDevelopmentGoalsWithCompetencyIds,
  mapCompetencyAverageAssessmentAnswersById,
} from "@apps/profile/career-tab/components/career-table-helpers";
import Competency from "@apps/profile/career-tab/components/competency";
import Responsibilities from "@apps/profile/career-tab/components/responsibilities";
import getCareerRoleRelatedDataQuery from "@apps/profile/career-tab/graphql/get-career-role-related-data-query";
import useLabel from "@apps/use-label/use-label";
import Avatar from "@components/avatar/avatar";
import Button from "@components/button/button";
import { ButtonSize } from "@components/button/button-constants";
import Layout from "@components/layout/layout";
import AppLink from "@components/link/link";
import Loading from "@components/loading/loading";
import { Select } from "@components/select/select";
import { TopicflowIcon } from "@components/topicflow-icon/topicflow-icon";
import { assertEdgesNonNull, getUrlUsingTypename } from "@helpers/helpers";

const AssessmentRolesAndReviewsSidebar = ({
  target,
}: {
  target: BasicUser;
}) => {
  const location = useLocation<TFLocationState>();
  const label = useLabel();
  const [selectedRoleId, setSelectedRoleId] = useState<number>(0);
  const [isRecentReviewsExpanded, setIsRecentReviewsExpanded] = useState(false);
  const [isRoleDetailsExpanded, setIsRoleDetailsExpanded] = useState(false);

  const { data, loading } = useQuery<
    GetUserRolesDetailsQueryQuery,
    GetUserRolesDetailsQueryQueryVariables
  >(getUserRolesDetailsQuery, {
    variables: { userId: target.id },
  });

  const currentRoles = useMemo(() => {
    const unsortedRoles = data?.user?.currentRoles || [];
    return sortBy(
      unsortedRoles,
      (role) => `${role.careerTrack.id}-${role.level}`,
    );
  }, [data]);
  const futureRoles = useMemo(() => {
    const unsortedRoles = data?.user?.futureRoles || [];
    return sortBy(
      unsortedRoles,
      (role) => `${role.careerTrack.id}-${role.level}`,
    );
  }, [data]);

  const options = useMemo(
    () => [
      ...currentRoles.map((role) => ({
        label: `Current Role: ${role.title} (Level ${role.level})`,
        value: role.id,
      })),
      ...futureRoles.map((role) => ({
        label: `Future Role: ${role.title} (Level ${role.level})`,
        value: role.id,
      })),
    ],
    [currentRoles, futureRoles],
  );

  const selectedRole = useMemo(
    () =>
      [...currentRoles, ...futureRoles].find(
        (role) => role.id === selectedRoleId,
      ),
    [currentRoles, futureRoles, selectedRoleId],
  );
  const competencies = selectedRole?.competencies
    ? assertEdgesNonNull(selectedRole.competencies)
    : [];
  const responsibilities = selectedRole?.responsibilities
    ? assertEdgesNonNull(selectedRole.responsibilities)
    : [];

  useEffect(() => {
    if (selectedRoleId === 0 && currentRoles.length > 0) {
      setSelectedRoleId(currentRoles[0].id);
    }
  }, [currentRoles, selectedRoleId]);

  const { data: roleData, loading: roleDataLoading } = useQuery<
    GetCareerRoleRelatedDataQueryQuery,
    GetCareerRoleRelatedDataQueryQueryVariables
  >(getCareerRoleRelatedDataQuery, {
    skip: !selectedRole,
    variables: {
      profileId: target.id,
      roleId: selectedRole?.id || 0,
    },
  });

  const developmentGoals = useMemo(() => {
    return mapCareerDevelopmentGoalsWithCompetencyIds(roleData);
  }, [roleData]);

  const currentRoleAverageAssessmentAnswersByCompetencyId = useMemo(() => {
    return mapCompetencyAverageAssessmentAnswersById(roleData);
  }, [roleData]);

  const recentReviews = useMemo(() => {
    return orderBy(
      (data?.assessmentDeliveries
        ? assertEdgesNonNull(data?.assessmentDeliveries)
        : []
      ).map((delivery) => ({
        ...delivery,
        orderValue: delivery.deliveryDatetime
          ? delivery.deliveryDatetime
          : delivery.lastSubmissionDatetime,
      })),
      ["orderValue"],
      ["desc"],
    );
  }, [data]);

  const profile = data?.user;

  return (
    <>
      <Layout.SidebarSection>
        <div className="flex items-center gap-2 text-gray-800">
          <div className="text-gray-900 font-medium">Viewing:</div>
          <Avatar user={target} size="5" />
          <div className="flex-1">{target.name}</div>
        </div>
      </Layout.SidebarSection>
      <Layout.SidebarSection
        title={`Recent ${label("review", {
          pluralize: true,
          titleCase: true,
        })}`}
        rightSide={
          <Button
            size={ButtonSize.medium}
            to="/assessments/delivered"
            text={`View All ${label("review", {
              capitalize: true,
              pluralize: true,
            })}`}
          />
        }
        isExpanded={isRecentReviewsExpanded}
        showExpansionControls
        onToggleExpand={(expanded) => setIsRecentReviewsExpanded(expanded)}
      >
        {recentReviews.length === 0 && (
          <div className="mt-2 text-gray-500">
            No recent {label("review", { pluralize: true })}.
          </div>
        )}
        <div className="mt-2 flex flex-col pb-2 empty:hidden">
          {recentReviews.map((review) => (
            <Layout.SidebarSubSectionListItem
              key={review.id}
              className="flex items-center justify-between"
            >
              <AppLink
                to={getUrlUsingTypename(review, location)}
                className="tf-header-sm hover:underline flex-1 flex items-center"
              >
                <TopicflowIcon
                  type="assessment"
                  className="mr-2 w-4 h-4 text-amber-700 shrink-0"
                />
                {review.complianceProgram?.title}
                <TopicflowIcon
                  type="externalLink"
                  className="ml-1 w-3 h-3 text-gray-500 shrink-0"
                />
              </AppLink>
              <div className="ml-2 text-gray-400 font-medium">
                {review.orderValue
                  ? moment(review.orderValue).format("MMM Do, YYYY")
                  : ""}
              </div>
            </Layout.SidebarSubSectionListItem>
          ))}
        </div>
      </Layout.SidebarSection>
      <Layout.SidebarSection
        title="Levelling Framework"
        isExpanded={isRoleDetailsExpanded}
        showExpansionControls
        onToggleExpand={(expanded) => setIsRoleDetailsExpanded(expanded)}
      >
        <div className="mt-6">
          {loading ? (
            <Loading>Loading roles...</Loading>
          ) : !selectedRole || currentRoles.length === 0 ? (
            <div className="text-gray-500">No roles found</div>
          ) : (
            <Select<number>
              value={selectedRoleId}
              options={options}
              onChange={(option) => setSelectedRoleId(option.value)}
            />
          )}
          <div className="mt-6 border-t empty:hidden">
            {roleDataLoading ? (
              <Loading className="py-6">Loading role...</Loading>
            ) : profile &&
              (competencies.length > 0 || responsibilities.length > 0) ? (
              <div className="flex flex-col divide-y">
                {competencies.map((competency) => (
                  <div key={competency.id} className="py-6">
                    <Competency
                      profile={profile}
                      competency={competency}
                      role={selectedRole}
                      goals={developmentGoals.filter(({ competencyIds }) =>
                        competencyIds.includes(competency.id),
                      )}
                      criteria={assertEdgesNonNull(competency.criteria)}
                      averageAssessmentAnswers={
                        currentRoleAverageAssessmentAnswersByCompetencyId[
                          competency.id
                        ]
                      }
                    />
                  </div>
                ))}
                {responsibilities.length > 0 && (
                  <div key={"responsibilities"} className="py-6">
                    <div className="font-medium text-gray-700 mb-2">
                      Responsibilities
                    </div>
                    <Responsibilities
                      careerRole={roleData?.careerRole}
                      profile={profile}
                      responsibilities={responsibilities}
                    />
                  </div>
                )}
              </div>
            ) : null}
          </div>
        </div>
      </Layout.SidebarSection>
    </>
  );
};

export default AssessmentRolesAndReviewsSidebar;
